import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/system'
import { formatAddress, convertShippingInfoToCityStateZip } from 'utils/data-manipulation/valueFormatter'
import { setDecimalPrecision } from 'utils/helpers'

const AbsoluteBox = styled(Box)(({ top, left, right }) => ({
  position: 'absolute',
  top: `${top}px`,
  ...(left && { left: `${left}px` }),
  ...(right && { right: `${right}px` }),
}))

const CheckTypography = styled(Typography)(() => ({
  fontSize: '14px',
}))

const CheckTemplate = ({
  checkRecipient,
  customerAddress,
  textAmount,
  amount,
  paymentDate,
  memo,
  checkSignatureImageSrc,
  checkLayoutOverride,
}) => {
  const { enableCustomCheckTemplates } = useSelector(state => state.featureFlags?.variables || {})

  const { checkLayout: checkLayoutSetting } = useSelector(
    state => state.settings?.settings?.purchaseSettings?.payment || {},
  )
  const checkLayout = checkLayoutOverride || checkLayoutSetting

  const renderCheck = useCallback(
    showCheck => {
      if (!showCheck) {
        return <Box sx={{ position: 'relative', width: '100%', height: '335px' }} />
      }
      return (
        <Box sx={{ position: 'relative', width: '100%', height: '335px' }}>
          {enableCustomCheckTemplates ? (
            <>
              <AbsoluteBox {...(checkLayout?.paymentDate || {})}>
                <CheckTypography>{paymentDate}</CheckTypography>
              </AbsoluteBox>
              <AbsoluteBox {...(checkLayout?.checkRecipient || {})}>
                <CheckTypography>{checkRecipient}</CheckTypography>
              </AbsoluteBox>
              <AbsoluteBox {...(checkLayout?.amount || {})}>
                <CheckTypography>{setDecimalPrecision(amount, { decimalPrecisionOverride: 2 })}</CheckTypography>
              </AbsoluteBox>
              <AbsoluteBox {...(checkLayout?.textAmount || {})}>
                <CheckTypography style={{ textTransform: 'capitalize' }}>{textAmount}</CheckTypography>
              </AbsoluteBox>
              <AbsoluteBox {...(checkLayout?.customerAddress || {})}>
                <CheckTypography style={{ whiteSpace: 'pre-line', lineHeight: '16px' }}>
                  {`${checkRecipient || ''}`}
                  <br />
                  {typeof customerAddress === 'string'
                    ? customerAddress
                    : `${formatAddress({
                        address: customerAddress?.address1,
                        address2: customerAddress?.address2,
                      })}
                    ${convertShippingInfoToCityStateZip(customerAddress)}
                  `}
                </CheckTypography>
              </AbsoluteBox>
              <AbsoluteBox {...(checkLayout?.memo || {})}>
                <CheckTypography>{memo}</CheckTypography>
              </AbsoluteBox>
              {checkSignatureImageSrc && (
                <AbsoluteBox {...(checkLayout?.checkSignature || {})}>
                  <img
                    src={checkSignatureImageSrc}
                    alt="check-signature"
                    style={{ height: 100, width: 200, objectFit: 'contain' }}
                  />
                </AbsoluteBox>
              )}
            </>
          ) : (
            <>
              <AbsoluteBox sx={{ top: '49px', right: '15px' }}>
                <CheckTypography>{paymentDate}</CheckTypography>
              </AbsoluteBox>
              <AbsoluteBox sx={{ top: '98px', left: '90px' }}>
                <CheckTypography>{checkRecipient}</CheckTypography>
              </AbsoluteBox>
              <AbsoluteBox sx={{ top: '99px', right: '20px' }}>
                <CheckTypography>{setDecimalPrecision(amount, { decimalPrecisionOverride: 2 })}</CheckTypography>
              </AbsoluteBox>
              <AbsoluteBox sx={{ top: '130px', left: '15px' }}>
                <CheckTypography style={{ textTransform: 'capitalize' }}>{textAmount}</CheckTypography>
              </AbsoluteBox>
              <AbsoluteBox sx={{ top: '170px', left: '90px' }}>
                <CheckTypography style={{ whiteSpace: 'pre-line', lineHeight: '16px' }}>
                  {`${checkRecipient || ''}`}
                  <br />
                  {typeof customerAddress === 'string'
                    ? customerAddress
                    : `${formatAddress({
                        address: customerAddress?.address1,
                        address2: customerAddress?.address2,
                      })}
                    ${convertShippingInfoToCityStateZip(customerAddress)}
                  `}
                </CheckTypography>
              </AbsoluteBox>
              <AbsoluteBox sx={{ top: '235px', left: '60px' }}>
                <CheckTypography>{memo}</CheckTypography>
              </AbsoluteBox>
              {checkSignatureImageSrc && (
                <AbsoluteBox sx={{ bottom: '50px', right: '40px' }}>
                  <img
                    src={checkSignatureImageSrc}
                    alt="check-signature"
                    style={{ height: 100, width: 200, objectFit: 'contain' }}
                  />
                </AbsoluteBox>
              )}
            </>
          )}
        </Box>
      )
    },
    [checkRecipient, paymentDate, amount, textAmount, memo, enableCustomCheckTemplates],
  )

  return (
    <>
      {renderCheck(true)}
      {renderCheck(false)}
      {renderCheck(true)}
    </>
  )
}

CheckTemplate.propTypes = {
  checkRecipient: PropTypes.string.isRequired,
  customerAddress: PropTypes.object,
  textAmount: PropTypes.string.isRequired,
  amount: PropTypes.string.isRequired,
  paymentDate: PropTypes.string.isRequired,
  memo: PropTypes.string.isRequired,
  checkSignatureImageSrc: PropTypes.string,
  checkLayoutOverride: PropTypes.object,
}

CheckTemplate.defaultProps = {
  customerAddress: {},
  checkSignatureImageSrc: '',
  checkLayoutOverride: null,
}

export default CheckTemplate
